/**
 * Terrain autotiles: the definitions are CONTENT (content/game/maps/autotiles.json), the mechanics are here.
 *
 * A definition says which tiles to draw for which autotile index; how those tiles are written
 * into a map stays in code, selected by the definition's `kind`:
 *
 *   bitmask   index -> one tile (grass, swamp, lava, the two Tiled-template sets)
 *   uniform   the same tile at every listed index (forest dirt: patches need no transitions)
 *   platform  a 23-entry variant list expanded into tiles WITH decorations: cliff faces one and two
 *             rows below the cell, a blocking (and optionally an occlusion) marker (cave walls,
 *             mountain cliffs)
 *   wall      a building wall material: per-index rules emitting tiles at offsets from the wall's
 *             cell, grouped under it as their parent (autotile_wall.ts; HouseBuilder runs them)
 *
 * Every definition has `id`, `name` and `kind`; every other field belongs to its kind, so a new
 * kind (a fragment-stamping tunnel, say) is a new entry shape and never a migration of the existing
 * ones. See "Walls" in docs/sprite-lab.md.
 *
 * This is a LEAF module on purpose. The map builder, the game client and Sprite Lab's browser
 * bundle all import it, so it must not reach for models, mechanics, the map builder or the file
 * system. The one per-cell decision the builder makes (`resolveAutotileCell`) and what it writes
 * for that decision (`autotileCellEffect` / `applyAutotileCellEffect`) live here so the Lab's
 * preview is the game's own code, not a copy kept in step by hand.
 *
 * The definitions arrive through the runtime content catalog (`contentCatalog.source.autotiles`), the
 * one import here outside the autotile modules. Editing them is a content save (Sprite Lab's
 * ContentRepository review/apply, or a hand edit) followed by `compile:content`; never a TS change.
 */
import { bindContent, contentCatalog } from "../content/catalog"
import { autotileIndexBitmask } from "./autotile_bitmask"
import { parseAutotileFile } from "./autotile_validation"
import type { WallAutotileDefinition } from "./autotile_wall"
import { wallRuleIndices } from "./autotile_wall"

// The validator is its own leaf (no catalog): the content compiler runs it before any catalog exists.
export { AUTOTILE_KINDS, PLATFORM_VARIANT_COUNT, parseAutotileFile, validateAutotileFile } from "./autotile_validation"
export type { AutotileValidationOptions } from "./autotile_validation"

export type {
    WallAlternative,
    WallAutotileDefinition,
    WallBuilderUtils,
    WallCellContext,
    WallCellDecorations,
    WallCellResolution,
    WallChoice,
    WallChoiceTrace,
    WallCondition,
    WallConditionTrace,
    WallEmit,
    WallOffset,
    WallResolveOptions,
    WallStep,
    WallWrite,
} from "./autotile_wall"
export { applyWallCell, isWallChoice, resolveWallCell, wallRule, wallRuleIndices } from "./autotile_wall"

// ---------------------------------------------------------------------------------------------
// Runtime shapes. These are what a map entry carries and what applyAutotiling reads; they moved
// here from base_map_builder.ts (which re-exports them) so that this module can stay a leaf.
// ---------------------------------------------------------------------------------------------

export interface AutotileDecoration {
    offsetX?: number
    offsetY?: number
    tile: number
    mapLayerYDelta?: number
    namedTileLayerId?: string
    tilesetId?: string
}

export interface AutotileSpec {
    tile: number | AutotileDecoration
    decorations?: AutotileDecoration[]
    noUndertile?: boolean
}

export type AutotileVariant = number | AutotileSpec

export type AutotileMap = {
    tilePos?: number
    tilesetId: string
    tiles: Record<number, AutotileVariant>
    autoTileFallback?: number
    mapLayerYDelta?: number
}

// ---------------------------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------------------------

/** The kinds that paint a map layer and go through the map builder's autotile pass. */
export type TerrainAutotileKind = "bitmask" | "uniform" | "platform"
export type AutotileKind = TerrainAutotileKind | "wall"

interface AutotileDefinitionBase {
    /** Stable id: a word (`grass`) or `<tileset>:<tilePos>` for sets keyed by their base tile. */
    id: string
    /** What the Lab shows. */
    name: string
    kind: AutotileKind
}

export interface BitmaskAutotileDefinition extends AutotileDefinitionBase {
    kind: "bitmask"
    tilesetId: string
    /** The tile painted to ask for this terrain; a fully surrounded cell (index 46) keeps it. */
    tilePos: number
    /** The layer the generators paint it on. Absent for Tiled-template sets: the template decides. */
    layerId?: string
    /** Autotile index (1-47) -> tile position in `tilesetId`. */
    tiles: Record<string, number>
    /** Drawn (as lpc-terrain) on map1 for an unmapped index; -1 there erases the cell. */
    fallback?: number
    mapLayerYDelta?: number
    /** Applied to this tile wherever a Tiled template paints it (feature_catalog.ts). */
    tiledTemplate?: boolean
    /** Why each index got its tile, shown by the Lab. */
    notes?: Record<string, string>
}

export interface UniformAutotileDefinition extends AutotileDefinitionBase {
    kind: "uniform"
    tilesetId: string
    layerId: string
    /** Indices drawn with the base tile; the base tile is chosen by the caller. */
    indices: number[]
    /** Base tile the Lab previews with. */
    previewTilePos: number
}

export interface PlatformAutotilePreview {
    layerId: string
    autoBlocking: boolean
    autoOcclusion: boolean
}

export interface PlatformAutotileDefinition extends AutotileDefinitionBase {
    kind: "platform"
    tilesetId: string
    tilePos: number
    /**
     * 23 tile positions, read by `platformAutotileMap`: 0-13 the rim and its two rows of cliff face,
     * 14-17 inner corners, 18 unused, 19-22 the notched corners. -1 = no tile.
     */
    variants: number[]
    /** Layer and flags the Lab previews with; callers pass their own. */
    preview?: PlatformAutotilePreview
}

/** A terrain definition: every kind but `wall`. */
export type AutotileDefinition = BitmaskAutotileDefinition | UniformAutotileDefinition | PlatformAutotileDefinition

/** Anything the definitions file can hold. */
export type AutotileFileEntry = AutotileDefinition | WallAutotileDefinition

export interface AutotileDefinitionFile {
    about?: string
    version: 1
    definitions: AutotileFileEntry[]
}

/**
 * One definitions file split the way callers use it: the TERRAIN definitions (every kind but `wall`,
 * painted on map layers) and the building WALL materials (resolved by HouseBuilder on its own grid),
 * each in file order. Pure, so live-content selectors can derive from a candidate catalog with it.
 */
export const autotileTables = (file: AutotileDefinitionFile) => {
    const parsed: readonly AutotileFileEntry[] = parseAutotileFile(file)
    return {
        file,
        terrain: parsed.filter((d): d is AutotileDefinition => d.kind !== "wall") as readonly AutotileDefinition[],
        walls: parsed.filter((d): d is WallAutotileDefinition => d.kind === "wall") as readonly WallAutotileDefinition[],
    }
}

// The definitions are content (content/game/maps/autotiles.json, a manifest input), published in the
// runtime catalog as `source.autotiles`; the compiler has already validated them against the tilesets.
// They follow live publications, so a wall material deployed from Sprite Lab is buildable at once.
// (Bound at the end of this file, once every helper the binding uses is defined.)
export let AUTOTILE_DEFINITION_FILE = contentCatalog.source.autotiles as AutotileDefinitionFile
export let AUTOTILE_DEFINITIONS: readonly AutotileDefinition[] = []
export let WALL_AUTOTILE_DEFINITIONS: readonly WallAutotileDefinition[] = []
/** The wall indices placement may compute (re-exported by autotiler.ts); see wallDrawableIndices. */
export let COMPUTABLE_WALL_AUTOTILE_INDICES = new Set<number>()

export const autotileDefinition = <T extends AutotileDefinition = AutotileDefinition>(id: string): T => {
    const definition = AUTOTILE_DEFINITIONS.find(d => d.id === id)
    if (!definition) throw new Error(`No autotile definition "${id}"`)
    return definition as T
}

export const wallAutotileDefinition = (id: string): WallAutotileDefinition => {
    const definition = WALL_AUTOTILE_DEFINITIONS.find(d => d.id === id)
    if (!definition) throw new Error(`No wall autotile definition "${id}"`)
    return definition
}

/**
 * Which wall material each item builds: every wall definition that names its `itemType`. HouseBuilder
 * resolves a wall cell's item through this, so a saved material becomes buildable with no code change.
 */
export const wallMaterialsByItem = (definitions: readonly WallAutotileDefinition[] = WALL_AUTOTILE_DEFINITIONS): Map<string, string> =>
    new Map(definitions.filter(d => d.itemType).map(d => [d.itemType, d.id]))

/**
 * The indices EVERY wall material can draw. Placement (building.ts, via autotiler.ts's
 * COMPUTABLE_WALL_AUTOTILE_INDICES) checks an index without asking which material sits on the cell,
 * so a shape is placeable only if no material would leave a cell of it undrawn.
 */
export const wallDrawableIndices = (definitions: readonly WallAutotileDefinition[] = WALL_AUTOTILE_DEFINITIONS): number[] => {
    if (definitions.length === 0) return []
    const [first, ...rest] = definitions.map(wallRuleIndices)
    return first.filter(index => rest.every(list => list.includes(index)))
}

const numericTiles = (tiles: Record<string, number>): Record<number, number> => {
    const out: Record<number, number> = {}
    Object.keys(tiles).forEach(index => (out[Number(index)] = tiles[index]))
    return out
}

// ---------------------------------------------------------------------------------------------
// Expansion: definition -> the AutotileMap a map entry carries
// ---------------------------------------------------------------------------------------------

/** index -> tile for a bitmask set. A fresh object each call, as the old literal tables were per use. */
export const bitmaskTiles = (definition: BitmaskAutotileDefinition): Record<number, number> => numericTiles(definition.tiles)

/** The uniform kind: `tilePos` at every listed index. */
export const uniformTiles = (definition: UniformAutotileDefinition, tilePos: number): Record<number, number> => {
    const tiles: Record<number, number> = {}
    definition.indices.forEach(index => (tiles[index] = tilePos))
    return tiles
}

/**
 * The platform kind's expansion (was the body of base_map_builder's `platformAutotile`; the
 * repeated cliff-face literals are folded into `faces`, producing the same objects in the same key
 * order -- autotile_characterization.test.ts pins that). The variant list is data; which index draws
 * which variant, with which cliff-face decorations below it, is this mechanic.
 */
export const platformAutotileMap = ({
    tile,
    tilesetId,
    variants,
    autoBlocking = true,
    autoOcclusion = false,
}: {
    tile: number
    tilesetId: string
    variants: number[]
    autoBlocking?: boolean
    autoOcclusion?: boolean
}): AutotileMap => {
    const blocking = autoBlocking
        ? {
              tilesetId: "lpc-terrain",
              namedTileLayerId: "blocking",
              tile: 121,
          }
        : undefined
    const occlusion = autoOcclusion
        ? {
              tilesetId: "lpc-terrain",
              namedTileLayerId: "occlusion",
              tile: 121,
          }
        : undefined

    const faces = (first: number, second: number, mapLayerYDelta?: number) => [
        variants[first] != -1
            ? {
                  ...(mapLayerYDelta === undefined ? {} : { mapLayerYDelta }),
                  offsetX: 0,
                  offsetY: 1,
                  tile: variants[first],
              }
            : undefined,
        variants[second] != -1
            ? {
                  ...(mapLayerYDelta === undefined ? {} : { mapLayerYDelta }),
                  offsetX: 0,
                  offsetY: 2,
                  tile: variants[second],
              }
            : undefined,
    ]

    const autotileMap: AutotileMap = {
        tilePos: tile,
        tilesetId,
        mapLayerYDelta: 2,
        tiles: {
            34: { tile: variants[0], decorations: [blocking, occlusion] },
            43: { tile: variants[1], decorations: [blocking, occlusion] },
            42: { tile: variants[1], decorations: [blocking, occlusion] },
            26: { tile: variants[2], decorations: [blocking, occlusion] },

            36: { tile: variants[3], decorations: [blocking, occlusion] },
            40: { tile: variants[3], decorations: [blocking, occlusion] },
            28: { tile: variants[4], decorations: [blocking, occlusion] },
            31: { tile: variants[4], decorations: [blocking, occlusion] },

            7: { tile: variants[5], decorations: [...faces(8, 11), blocking, occlusion] },
            12: { tile: variants[6], decorations: [...faces(9, 12), blocking, occlusion] },
            25: { tile: variants[6], decorations: [...faces(9, 12), blocking, occlusion] },
            4: { tile: variants[7], decorations: [...faces(10, 13), blocking, occlusion] },

            33: { tile: variants[14], decorations: [...faces(9, 12, -1), blocking, occlusion] },
            41: { tile: variants[15], decorations: [...faces(9, 12, -1), blocking, occlusion] },

            44: { tile: variants[16], decorations: [blocking, occlusion] },
            45: { tile: variants[17], decorations: [blocking, occlusion] },

            // top left
            35: { tile: variants[19] },
            37: { tile: variants[19] },

            // top right
            27: { tile: variants[20], decorations: blocking ? [blocking] : undefined },
            29: { tile: variants[20], decorations: [blocking, occlusion] },

            // bottom left
            32: { tile: variants[21], decorations: [...faces(8, 11), blocking, occlusion] },
            11: { tile: variants[21], decorations: [...faces(8, 11), blocking, occlusion] },
            20: { tile: variants[21], decorations: [...faces(8, 11), blocking, occlusion] },

            // bottom right
            10: { tile: variants[22], decorations: [...faces(10, 13), blocking, occlusion] },
            17: { tile: variants[22], decorations: [...faces(10, 13), blocking, occlusion] },
        },
    }

    Object.values(autotileMap.tiles).forEach((t: AutotileSpec) => (t.noUndertile = true))

    return autotileMap
}

export interface AutotileMapOptions {
    /** uniform: the base tile. Defaults to the definition's preview tile. */
    tilePos?: number
    /** platform: defaults to the definition's preview flags, else the builder's defaults. */
    autoBlocking?: boolean
    autoOcclusion?: boolean
}

/**
 * The AutotileMap a map entry carries for this definition -- exactly what the code that used the
 * old tables built: feature_catalog for a Tiled-template set (`{tilesetId, tiles}` only, so no
 * `tilePos` and no fallback), createAutotileEntry for the others, platformAutotile for a platform.
 */
export const autotileMapFor = (definition: AutotileDefinition, options: AutotileMapOptions = {}): AutotileMap => {
    switch (definition.kind) {
        case "bitmask":
            if (definition.tiledTemplate) {
                return { tilesetId: definition.tilesetId, tiles: bitmaskTiles(definition) }
            }
            return {
                tilePos: definition.tilePos,
                tilesetId: definition.tilesetId,
                tiles: bitmaskTiles(definition),
                autoTileFallback: definition.fallback,
                ...(definition.mapLayerYDelta === undefined ? {} : { mapLayerYDelta: definition.mapLayerYDelta }),
            }
        case "uniform": {
            const tilePos = options.tilePos ?? definition.previewTilePos
            return {
                tilePos,
                tilesetId: definition.tilesetId,
                tiles: uniformTiles(definition, tilePos),
                autoTileFallback: tilePos,
            }
        }
        case "platform":
            return platformAutotileMap({
                tile: definition.tilePos,
                tilesetId: definition.tilesetId,
                variants: definition.variants,
                autoBlocking: options.autoBlocking ?? definition.preview?.autoBlocking,
                autoOcclusion: options.autoOcclusion ?? definition.preview?.autoOcclusion,
            })
    }
}

/** The tile painted to ask for this definition. */
export const autotileBaseTile = (definition: AutotileDefinition, options: AutotileMapOptions = {}) =>
    definition.kind === "uniform" ? options.tilePos ?? definition.previewTilePos : definition.tilePos

/** The layer the definition is painted on: its own, else its preview layer, else map1. */
export const autotileSourceLayer = (definition: AutotileDefinition): string =>
    definition.kind === "platform" ? definition.preview?.layerId || "map1" : definition.layerId || "map1"

// ---------------------------------------------------------------------------------------------
// The per-cell decision (BaseMapBuilder.applyAutotiling calls this)
// ---------------------------------------------------------------------------------------------

export interface AutotileDraw {
    /** The tileset the variant's numbers are positions in. */
    tilesetId: string
    variant: AutotileVariant
    /** The autotile index, or -1 for the map1 fallback. Drives the "undertile" copy. */
    variantPos: number
    layerUpNumber: number
    /** Where the variant's own tile goes. */
    layerUpId: string
}

export type AutotileCellDecision =
    /** Unmapped 0 (empty) or 46 (surrounded): nothing is drawn, nothing is logged; the cell keeps its tile. */
    | { kind: "skip"; index: number }
    /** The definition names a tile (or tile spec) for this index. */
    | ({ kind: "mapped"; index: number } & AutotileDraw)
    /** Unmapped on map1: lpc-terrain's `autoTileFallback || 100` goes up a layer. Logged as a miss. */
    | ({ kind: "fallback"; index: number } & AutotileDraw)
    /** Unmapped on any other layer: drawn as -1, which ERASES the cell. Logged as a miss. */
    | ({ kind: "miss"; index: number } & AutotileDraw)

/** The layer number of a `mapN` layer id (map1 -> 1). Other layers have none. */
export const mapLayerNumber = (layerId: string): number | undefined => {
    const match = /^map(\d+)$/.exec(layerId)
    return match ? Number(match[1]) : undefined
}

/**
 * What the map builder draws for one cell of an autotiled layer, given the cell's autotile index.
 *
 * `layerNumber` is the source layer's number (map3 -> 3); the variant goes `mapLayerYDelta || 1`
 * layers up. The asymmetry between map1 and every other layer is the shipped behaviour, kept
 * exactly: an index the definition does not name draws the fallback on map1 but erases elsewhere.
 */
export const resolveAutotileCell = (
    spec: AutotileMap,
    layerId: string,
    layerNumber: number,
    index: number,
): AutotileCellDecision => {
    const layerUpNumber = layerNumber + (spec.mapLayerYDelta || 1)
    const layerUpId = `map${layerUpNumber}`
    const variant: AutotileVariant = spec.tiles[index]
    const layerIdToUse =
        typeof variant === "number"
            ? layerUpId
            : typeof variant?.tile === "number"
            ? layerUpId
            : (variant?.tile as AutotileDecoration)?.namedTileLayerId || layerUpId

    if (variant !== undefined) {
        return { kind: "mapped", index, tilesetId: spec.tilesetId, variant, variantPos: index, layerUpNumber, layerUpId: layerIdToUse }
    }
    if (index === 46 || index === 0) {
        return { kind: "skip", index }
    }
    if (layerId === "map1") {
        return {
            kind: "fallback",
            index,
            tilesetId: "lpc-terrain",
            variant: spec.autoTileFallback || 100,
            variantPos: -1,
            layerUpNumber,
            layerUpId: layerIdToUse,
        }
    }
    return { kind: "miss", index, tilesetId: spec.tilesetId, variant: -1, variantPos: index, layerUpNumber, layerUpId: layerIdToUse }
}

// ---------------------------------------------------------------------------------------------
// What a drawn cell writes (BaseMapBuilder.finalize's autotile callback runs this)
// ---------------------------------------------------------------------------------------------

export interface TileRef {
    tilesetId: string
    tilePos: number
}

export type AutotileOp =
    /** Write a tile (or clear the cell, `tile: null`) in the ROOM's layer: what ends up in the map. */
    | { op: "set"; layerId: string; dx: number; dy: number; tile: TileRef | null; role: "art" | "decoration" | "clear" }
    /** Write the builder's global autotile INPUT layer, which later layers' autotile passes read. */
    | { op: "input"; layerId: string; dx: number; dy: number; tile: TileRef | null }
    /** Copy the source layer's tile at (fromDx, fromDy) into the cell, unless that neighbour is empty. */
    | { op: "undertile"; layerId: string; fromDx: number; fromDy: number }

// The neighbour whose ground goes UNDER an edge tile once the edge has moved up a layer.
const undertileOffset = (variantPos: number): [number, number] => {
    switch (variantPos) {
        case 2:
        case 3:
        case 15:
        case 27:
        case 28:
        case 31:
            return [1, 0]
        case 26:
        case 44:
            return [1, -1]
        case 12:
        case 13:
            return [0, 1]
        case 5:
        case 8:
        case 10:
        case 42:
            return [0, -1]
        case 36:
            return [-1, 0]
        case 25:
        case 1:
            return [-1, 0]
        case 4:
        case 33:
            return [1, 1]
        case 45:
            return [-1, -1]
        case 11:
        case 41:
            return [-1, 1]
        case 7:
        case 17:
        case 35:
        case 14:
        case 18:
        case 20:
        case 29:
        case 40:
        case 34:
        case 32:
            return [-1, 0]
    }
    return [0, 0]
}

/**
 * The writes one drawn cell makes, in the order the builder makes them. `sourceLayerId` is the
 * layer the autotiled tile was painted on.
 */
export const autotileCellEffect = (draw: AutotileDraw, sourceLayerId: string): AutotileOp[] => {
    const { tilesetId, variant, variantPos, layerUpNumber, layerUpId } = draw
    const tileMeta: AutotileDecoration | number = typeof variant === "number" ? variant : variant.tile
    const decorations: AutotileDecoration[] = (typeof variant === "number" ? [] : variant.decorations || []).filter(n => !!n)
    const baseTile = typeof tileMeta === "number" ? tileMeta : tileMeta?.tile

    if (baseTile === -1) {
        // erase
        return [
            { op: "input", layerId: layerUpId, dx: 0, dy: 0, tile: null },
            { op: "set", layerId: sourceLayerId, dx: 0, dy: 0, tile: null, role: "clear" },
        ]
    }
    const art: TileRef = { tilesetId, tilePos: baseTile }
    const ops: AutotileOp[] = [
        // paste it into the next layer up
        { op: "input", layerId: layerUpId, dx: 0, dy: 0, tile: art },
        { op: "set", layerId: layerUpId, dx: 0, dy: 0, tile: art, role: "art" },
        // remove original
        { op: "set", layerId: sourceLayerId, dx: 0, dy: 0, tile: null, role: "clear" },
    ]
    decorations.forEach(decoration => {
        ops.push({
            op: "set",
            layerId: decoration.namedTileLayerId || `map${(decoration.mapLayerYDelta || 0) + layerUpNumber}`,
            dx: decoration.offsetX || 0,
            dy: decoration.offsetY || 0,
            tile: { tilesetId: decoration.tilesetId || tilesetId, tilePos: decoration.tile },
            role: "decoration",
        })
    })
    const noUndertile = typeof variant === "number" ? false : variant.noUndertile
    if (!noUndertile) {
        const [fromDx, fromDy] = undertileOffset(variantPos)
        if (fromDx || fromDy) ops.push({ op: "undertile", layerId: sourceLayerId, fromDx, fromDy })
    }
    return ops
}

/** Where a cell effect is written. The builder stores absolute gids; the Lab stores whatever it likes. */
export interface AutotileStore<T> {
    encode: (tile: TileRef) => T
    empty: T
    set: (layerId: string, x: number, y: number, value: T) => void
    get: (layerId: string, x: number, y: number) => T
    setInput: (layerId: string, x: number, y: number, value: T) => void
}

export const applyAutotileCellEffect = <T>(store: AutotileStore<T>, ops: AutotileOp[], x: number, y: number): void => {
    ops.forEach(op => {
        switch (op.op) {
            case "input":
                store.setInput(op.layerId, x + op.dx, y + op.dy, op.tile ? store.encode(op.tile) : store.empty)
                break
            case "set":
                store.set(op.layerId, x + op.dx, y + op.dy, op.tile ? store.encode(op.tile) : store.empty)
                break
            case "undertile": {
                const replaceTile = store.get(op.layerId, x + op.fromDx, y + op.fromDy)
                if (replaceTile !== store.empty) {
                    store.set(op.layerId, x, y, replaceTile)
                }
                break
            }
        }
    })
}

// ---------------------------------------------------------------------------------------------
// Reading an index (for the Lab)
// ---------------------------------------------------------------------------------------------

export type AutotileSide = "N" | "E" | "S" | "W"
export type AutotileCorner = "NW" | "NE" | "SW" | "SE"

/**
 * An index read back into its neighbourhood. A RIM is a side whose orthogonal neighbour is empty
 * (the edge the art must draw); a NOTCH is a clear diagonal between two filled orthogonals (a
 * concave corner). Index 47 is a lone cell, all rims; 46 is fully surrounded, neither.
 */
export const decodeAutotileIndex = (index: number): { sum: number; rims: AutotileSide[]; notches: AutotileCorner[] } | undefined => {
    const sum = autotileIndexBitmask(index)
    if (sum === undefined) return undefined
    const has = (bit: number) => (sum & bit) !== 0
    const [NW, N, NE, W, E, SW, S, SE] = [1, 2, 4, 8, 16, 32, 64, 128]
    const rims: AutotileSide[] = []
    if (!has(N)) rims.push("N")
    if (!has(E)) rims.push("E")
    if (!has(S)) rims.push("S")
    if (!has(W)) rims.push("W")
    const notches: AutotileCorner[] = []
    if (has(N) && has(W) && !has(NW)) notches.push("NW")
    if (has(N) && has(E) && !has(NE)) notches.push("NE")
    if (has(S) && has(W) && !has(SW)) notches.push("SW")
    if (has(S) && has(E) && !has(SE)) notches.push("SE")
    return { sum, rims, notches }
}

// ---------------------------------------------------------------------------------------------
// What a definition names (for whoever allocates cells, or lists a definition's pieces)
// ---------------------------------------------------------------------------------------------

/**
 * Every tile an entry of the definitions file can draw or names, each once, in no particular order:
 * its own tiles in its tileset, plus the tiles its mechanic stamps from elsewhere (a bitmask set's
 * map1 fallback is drawn from lpc-terrain; a platform's blocking/occlusion markers are lpc-terrain
 * 121; a wall's cell decorations name their own tileset). A uniform set's base tile is the caller's,
 * so only its preview tile is named. Sprite Lab's autotile save reads this to keep allocated cells
 * clear of every definition.
 */
export const autotileTileReferences = (entry: AutotileFileEntry): TileRef[] => {
    const refs = new Map<string, TileRef>()
    const add = (tilesetId: string, tilePos: unknown) => {
        if (typeof tilePos === "number" && Number.isInteger(tilePos) && tilePos >= 0) refs.set(`${tilesetId}:${tilePos}`, { tilesetId, tilePos })
    }
    switch (entry.kind) {
        case "bitmask":
            add(entry.tilesetId, entry.tilePos)
            Object.values(entry.tiles || {}).forEach(tile => add(entry.tilesetId, tile))
            if (entry.fallback !== undefined) add("lpc-terrain", entry.fallback)
            break
        case "uniform":
            add(entry.tilesetId, entry.previewTilePos)
            break
        case "platform":
            add(entry.tilesetId, entry.tilePos)
            ;(entry.variants || []).forEach(tile => add(entry.tilesetId, tile))
            add("lpc-terrain", 121)
            break
        case "wall": {
            const emit = (emits: { tile: number }[]) => emits.forEach(e => add(entry.tilesetId, e.tile))
            Object.values(entry.rules || {}).forEach(steps =>
                steps.forEach(step => ("choose" in step ? step.choose.forEach(alternative => emit(alternative.emit || [])) : emit([step]))),
            )
            Object.values(entry.cell || {}).forEach(ref => ref && add(ref.tilesetId, ref.tilePos))
            break
        }
    }
    return [...refs.values()]
}

// ---------------------------------------------------------------------------------------------
// Adapters for the old exports (AutoTiles, PlatformAutoTiles, AutotileConfigs)
// ---------------------------------------------------------------------------------------------

/** `AutoTiles`: tileset -> base tile -> index -> tile, for the Tiled-template sets. */
export const tiledTemplateAutoTiles = (): Record<string, Record<number, Record<number, number>>> => {
    const out: Record<string, Record<number, Record<number, number>>> = {}
    AUTOTILE_DEFINITIONS.forEach(definition => {
        if (definition.kind === "bitmask" && definition.tiledTemplate) {
            out[definition.tilesetId] = out[definition.tilesetId] || {}
            out[definition.tilesetId][definition.tilePos] = bitmaskTiles(definition)
        }
    })
    return out
}

/** `PlatformAutoTiles`: tileset -> base tile -> the 23 variants. */
export const platformVariantTable = (): Record<string, Record<number, number[]>> => {
    const out: Record<string, Record<number, number[]>> = {}
    AUTOTILE_DEFINITIONS.forEach(definition => {
        if (definition.kind === "platform") {
            out[definition.tilesetId] = out[definition.tilesetId] || {}
            out[definition.tilesetId][definition.tilePos] = [...definition.variants]
        }
    })
    return out
}

/** The `AutotileConfig` shape autotiling_utils.ts has always exported, for a generator-painted set. */
export const generatorAutotileConfig = (id: string) => {
    const definition = autotileDefinition<BitmaskAutotileDefinition>(id)
    if (definition.kind !== "bitmask" || !definition.layerId) {
        throw new Error(`Autotile definition "${id}" is not a generator-painted bitmask set`)
    }
    return {
        tilesetId: definition.tilesetId,
        tilePos: definition.tilePos,
        layerId: definition.layerId,
        autotileMap: bitmaskTiles(definition),
        autoTileFallback: definition.fallback,
    }
}

/** forestDirt(baseTileId): the uniform set around a caller-chosen base tile. */
export const uniformAutotileConfig = (id: string, tilePos: number) => {
    const definition = autotileDefinition<UniformAutotileDefinition>(id)
    if (definition.kind !== "uniform") throw new Error(`Autotile definition "${id}" is not a uniform set`)
    return {
        tilesetId: definition.tilesetId,
        tilePos,
        layerId: definition.layerId,
        autotileMap: uniformTiles(definition, tilePos),
        autoTileFallback: tilePos,
    }
}

bindContent(catalog => {
    const tables = autotileTables(catalog.source.autotiles as AutotileDefinitionFile)
    return { ...tables, computable: new Set<number>(wallDrawableIndices(tables.walls)) }
}, next => {
    AUTOTILE_DEFINITION_FILE = next.file
    AUTOTILE_DEFINITIONS = next.terrain
    WALL_AUTOTILE_DEFINITIONS = next.walls
    COMPUTABLE_WALL_AUTOTILE_INDICES = next.computable
})
